<template>
  <TvBase storage-key="tv2id">
    <div v-if="state === 'INTERSTICIEL'" class="view tv dark">
      <IntersticeTwo />
    </div>
    <div v-if="state !== 'INTERSTICIEL' && screenData" class="view tv dark">
      <Screen v-if="state === ''" :data="screen1">
        <StudyParameters :data="screenData" />
      </Screen>
      <Screen v-if="state === 'SYNTHESIS'" :data="screen2">
        <TableBlock :data="screen2.data" :index="index" :scrollIndex="scrollIndex" />
      </Screen>
      <Screen v-if="state === 'IMPACT'" :data="screen3">
        <TableBlock :data="screen3.data" :index="index" :scrollIndex="scrollIndex" />
      </Screen>
      <Screen v-if="state === 'TRANSIT' && distribution === 'LOCALISATION'">
        <TableBlock :data="screen4.data" :index="index" :scrollIndex="scrollIndex" />
      </Screen>
      <Screen v-if="state === 'TRANSIT' && distribution === 'TIME'" :data="screen5">
        <TableBlock :data="screen5.data" :index="index" :scrollIndex="scrollIndex" />
      </Screen>
      <Screen v-if="state === 'TRANSIT' && distribution === 'PERCENTAGE'" :data="screen7">
        <TableBlock :data="screen7.data" :index="index" :scrollIndex="scrollIndex" />
      </Screen>
      <Screen v-if="state === 'OUTLIER'" :data="screen6">
        <TableBlock :data="screen6.data" :index="index" :scrollIndex="scrollIndex" />
      </Screen>
      <Screen v-if="state === 'ENVIRONMENTAL'" :data="environmental">
        <div class="container">
          <div class="current-site-container">
            <div class="current-site-title">{{ $t('actualSites', 0) }}</div>
            <div class="current-site-value">
              <b>{{ environmental.data.currentSite.toFixed(environmental.data.toFixedValue) }}</b> kg
            </div>
          </div>
          <TableBlock :data="environmental.data" :index="index" :scrollIndex="scrollIndex" />
        </div>
      </Screen>
    </div>
  </TvBase>
</template>

<script>
import Tv from './TvMixin';
import { extendedTimeRangesCaptions, timeRangesCaptions } from '@/pure/ComputedData';

export default {
  name: 'Tv2',
  mixins: [Tv],

  data() {
    return {
      state: 'INTERSTICIEL',
      lastState: '',
      distribution: 'LOCALISATION',
      environmentalMode: 0,
      index: -1,
      scrollIndex: 0,
      screenData: null,
    };
  },

  computed: {
    screen1() {
      const { collaboratorsCount, unrecognizedCount, offLimitCount, excludedAddresses } = this.screenData;
      return {
        heading: {
          caption: this.$t('settingHelper.studyParameters'),
          icon: 'cog',
        },
        footer: {
          entries: [
            {
              value: collaboratorsCount,
              description: this.$t('parameters.totalCollaborators', collaboratorsCount),
            },
            {
              value: collaboratorsCount - unrecognizedCount - offLimitCount,
              description: this.$t('parameters.accountedCollaborators', collaboratorsCount - unrecognizedCount - offLimitCount),
            },
            {
              value: unrecognizedCount,
              description: this.$t('parameters.unrecognizedAddress', unrecognizedCount),
            },
            {
              value: offLimitCount,
              description: this.$t('parameters.offlimitAddress', offLimitCount),
            },
            {
              value: excludedAddresses,
              description: this.$t('parameters.excludedAddress', excludedAddresses),
            },
          ],
        },
      };
    },

    screen2() {
      const { filteredCollaboratorsCount, sites } = this.screenData;
      const value = filteredCollaboratorsCount;
      return {
        heading: {
          caption: this.$t('sections.impact'),
          icon: 'happy',
          baseline: {
            value,
            caption: `${this.$t('collaboratorCount')} :`,
          },
        },
        data: {
          entries: sites.map(site => {
            const { impact } = site.details;
            return [
              {
                type: 'sub-header',
                caption: `${this.$t('improvements')} :`,
              },
              {
                entries: [
                  {
                    value: impact.improvement.count,
                    icon: 'face',
                  },
                  {
                    value: filteredCollaboratorsCount ? Math.round((impact.improvement.count / filteredCollaboratorsCount) * 100) : 0,
                    sub: '%',
                  },
                ],
              },
              {
                type: 'sub-header',
                caption: `${this.$t('degradations')} :`,
              },
              {
                entries: [
                  {
                    value: impact.decay.count,
                    icon: 'face',
                  },
                  {
                    value: filteredCollaboratorsCount ? Math.round((impact.decay.count / filteredCollaboratorsCount) * 100) : 0,
                    sub: '%',
                  },
                ],
              },
            ];
          }),
        },
      };
    },

    screen3() {
      const { filteredCollaboratorsCount, sites } = this.screenData;
      const value = filteredCollaboratorsCount;
      return {
        heading: {
          caption: this.$t('degradations'),
          icon: 'sad',
          baseline: {
            value,
            caption: `${this.$t('collaboratorCount')} :`,
          },
        },
        data: {
          specifications: [
            {
              type: 'heading',
              caption: `${this.$t('collaboratorsNumber')} :`,
            },
            {
              type: 'heading',
              caption: `${this.$t('averageAdditionalTime')} :`,
            },
          ],
          entries: this.computeImpact(sites, 'decay', true),
        },
      };
    },

    screen4() {
      const { collaboratorsByRegion, sites } = this.screenData;
      const subHeaderCaption = this.$t('CurrentSiteName');
      const subHeader = {
        type: 'sub-header',
        caption: subHeaderCaption,
      };
      const superHeader = [subHeader];

      const specifications = [subHeader];
      collaboratorsByRegion.slice(0, 4).forEach(region => {
        superHeader.push({
          caption: region.caption,
          value: region.percentage,
          sub: '%',
        });
        specifications.push({
          value: region.averageTime,
          sub: ' min',
        });
      });
      const entries = sites.map(site => {
        const result = [subHeader];
        site.details.collaboratorsByRegion
          .sort((a, b) => {
            return b.count - a.count;
          })
          .slice(0, 4)
          .forEach((item, idx) => result.push(this.computeTrend(item.averageTime - collaboratorsByRegion[idx].averageTime)));
        return result;
      });

      const data = {
        superHeader,
        specifications,
        entries,
      };
      return {
        data,
      };
    },

    screen5() {
      const { filteredCollaboratorsCount, collaboratorsByTimeRanges, sites, useExtendedTimeRanges } = this.screenData;
      const value = filteredCollaboratorsCount;
      const supHeaderCaption = useExtendedTimeRanges ? extendedTimeRangesCaptions : timeRangesCaptions;
      const subInfo = {
        supHeaderCaption,
        subHeader: this.$t('CurrentSiteName'),
      };
      const entries = sites.map(site => {
        const result = [];
        if (subInfo.subHeader) {
          result.push({
            type: 'sub-header',
            caption: subInfo.subHeader,
          });
        }
        site.details.collaboratorsByTimeRanges.forEach(item => result.push({ value: item.count }));
        return result;
      });
      const data = this.computeTimeTable(
        collaboratorsByTimeRanges.map(item => item.count),
        entries,
        subInfo,
      );
      return {
        heading: {
          caption: this.$t('collaboratorsByAccessTime'),
          icon: 'clock',
          baseline: {
            value,
            caption: `${this.$t('collaboratorCount')} :`,
          },
        },
        data,
      };
    },

    screen6() {
      const { filteredCollaboratorsCount, sites } = this.screenData;
      const value = filteredCollaboratorsCount;
      const entries = this.comuputesensibleCaseDecay().map((row, idx) => {
        const site = sites[idx];
        row.unshift(this.buildSiteBlock(site, idx));
        row.push({
          value: site.details.impact.sensibleCaseDecay.averageTime,
          sub: ' min',
        });
        return row;
      });
      return {
        heading: {
          caption: this.$t('sections.outlier'),
          icon: 'alert',
          baseline: {
            value,
            caption: `${this.$t('collaboratorCount')} :`,
          },
        },
        data: {
          specifications: [
            {
              className: {
                big: true,
              },
            },
            {
              type: 'heading',
              caption: `${this.$t('collaboratorsNumber')} :`,
            },
            {
              type: 'heading',
              caption: `${this.$t('averageAccessTime')} :`,
            },
          ],
          entries,
        },
      };
    },

    screen7() {
      const { filteredCollaborators, filteredCollaboratorsCount, collaboratorsByTimeRanges, sites, useExtendedTimeRanges } = this.screenData;
      const percentMode = this.distribution === 'PERCENTAGE';
      const value = filteredCollaboratorsCount;
      const supHeaderCaption = useExtendedTimeRanges ? extendedTimeRangesCaptions : timeRangesCaptions;
      const subInfo = { supHeaderCaption, subHeader: this.$t('CurrentSiteName') };

      const entries = sites.map(site => {
        let result = [];

        if (subInfo.subHeader) {
          result.push({ type: 'sub-header', caption: subInfo.subHeader });
        }

        if (percentMode) {
          let percentages = [];
          site.details.collaboratorsByTimeRanges.forEach((item, index) =>
            percentages.push({
              index: index,
              ...item,
              percentage: filteredCollaborators.length ? (item.count / filteredCollaborators.length) * 100 : 0,
            }),
          );

          const diff = 100 - percentages.map(e => Math.floor(e.percentage)).reduce((acc, val) => acc + val, 0);
          const sortedPercentages = percentages.sort((a, b) => Math.floor(a.percentage) - a.percentage - (Math.floor(b.percentage) - b.percentage));
          const percentageResult = sortedPercentages.map((e, index) => {
            const percentage = index < diff ? Math.floor(e.percentage) + 1 : Math.floor(e.percentage);
            return { ...e, value: `${percentage}%` };
          });

          result = [...result, ...percentageResult.sort((a, b) => a.index - b.index)];
        } else {
          site.details.collaboratorsByTimeRanges.forEach(item => result.push({ value: item.count }));
        }

        return result;
      });

      const data = percentMode
        ? this.computeTimeTable(
            collaboratorsByTimeRanges.map(item => `${((item.count / filteredCollaborators.length) * 100).toFixed(0)}%`),
            entries,
            subInfo,
          )
        : this.computeTimeTable(
            collaboratorsByTimeRanges.map(item => item.count),
            entries,
            subInfo,
          );

      const title = percentMode ? this.$t('collaboratorsByAccessTime') : this.$t('collaboratorsByAccessTimePercentage');

      return {
        heading: {
          caption: title,
          icon: 'clock',
          baseline: {
            value,
            caption: `${this.$t('collaboratorCount')} :`,
          },
        },
        data,
      };
    },

    environmental() {
      const { sites, environmentalImpact, environmentalImpactWeighted, filteredCollaboratorsCount } = this.screenData;
      const value = filteredCollaboratorsCount;

      const impact = this.environmentalMode === 2 ? environmentalImpactWeighted : environmentalImpact;
      const trips = this.environmentalMode === 1 ? 1 : 436;

      const entries = sites.map((site, idx) => {
        const evolution =
          this.environmentalMode === 2
            ? environmentalImpactWeighted > 0
              ? Math.round(((site.details.environmentalImpactWeighted - environmentalImpactWeighted) / environmentalImpactWeighted) * 100 * 100) / 100
              : 0
            : environmentalImpact > 0
              ? Math.round(((site.details.environmentalImpact - environmentalImpact) / environmentalImpact) * 100 * 100) / 100
              : 0;

        return [
          this.buildSiteBlock(site, idx),
          {
            value: (impact * trips) / filteredCollaboratorsCount,
            toFixedValue: this.environmentalMode === 1 ? 2 : 0,
            sub: ' kg',
          },
          {
            trend: evolution === 0 ? 0 : evolution < 0 ? 'down' : 'up',
            value: Math.abs(evolution),
            sub: '%',
          },
        ];
      });

      return {
        heading: {
          caption: this.$t('sections.environmental'),
          icon: 'environment',
          baseline: {
            value,
            caption: `${this.$t('collaboratorCount')} :`,
          },
        },
        data: {
          entries,
          currentSite: (impact * trips) / filteredCollaboratorsCount,
          toFixedValue: this.environmentalMode === 1 ? 2 : 0,
        },
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  .current-site-container {
    width: 100%;
    display: flex;
    border-bottom: solid 2px #fff;

    .current-site-title {
      width: 20%;
      margin: 24px 5% 24px 25%;
      text-align: center;
      font-size: 32px;
    }
    .current-site-value {
      margin: 24px 30% 24px 8%;
      font-size: 32px;

      b {
        font-size: 48px;
        line-height: 32px;
        font-weight: 800;
      }
    }
  }
}
</style>
